import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Plus, Printer, Palette, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

const PALETTE = [
  "#0f2b6b", "#C9A227", "#059669", "#dc2626",
  "#7c3aed", "#0891b2", "#ea580c", "#db2777",
  "#0284c7", "#65a30d", "#a16207", "#4b5563",
];

function ColorSwatch({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-full ring-2 transition-all ${
            value === c ? "ring-foreground ring-offset-2 ring-offset-background scale-110" : "ring-transparent"
          }`}
          style={{ backgroundColor: c }}
          aria-label={`Choose color ${c}`}
        />
      ))}
    </div>
  );
}

export default function AdvisorsPage() {
  const [advisors, setAdvisors] = useState([]);
  const [metricsByAdvisor, setMetricsByAdvisor] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [window, setWindow] = useState("mtd");

  const load = async () => {
    try {
      const [advRes, metricsRes] = await Promise.all([
        api.get("/advisors"),
        api.get(`/metrics/by-advisor?window=${window}`).catch(() => ({ data: [] })),
      ]);
      setAdvisors(advRes.data);
      // Map advisor_id → metrics for MTD/YTD tiles next to the name
      const map = {};
      (metricsRes.data || []).forEach((m) => { map[m.advisor_id] = m; });
      setMetricsByAdvisor(map);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/advisors", {
        name,
        email: email || null,
        phone: phone || null,
        color,
      });
      setName("");
      setEmail("");
      setPhone("");
      setColor(PALETTE[0]);
      toast.success("Advisor added");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const updateColor = async (id, c) => {
    try {
      await api.patch(`/advisors/${id}`, { color: c });
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/advisors/${id}`);
      toast.success("Advisor removed");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <DashboardShell>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-label text-muted-foreground">Team</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Service Advisors</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Assign each advisor a color chip — it shows up on the dashboard, segments, and reports so you can
            spot who owns which customer at a glance.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} className="no-print" data-testid="print-advisors-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print roster
        </Button>
      </div>

      {/* Date range picker — every KPI on this page recomputes when this changes */}
      <div className="mb-4 flex items-center gap-2 flex-wrap no-print" data-testid="advisor-window-picker">
        <span className="font-mono-label text-[0.62rem] tracking-widest text-muted-foreground">
          TIME FRAME:
        </span>
        {[
          { key: "7d",  label: "Last 7d" },
          { key: "30d", label: "Last 30d" },
          { key: "mtd", label: "This month" },
          { key: "ytd", label: "Year to date" },
          { key: "all", label: "All time" },
        ].map((w) => (
          <button
            key={w.key}
            onClick={() => setWindow(w.key)}
            className={`px-3 py-1 rounded-full text-[0.7rem] font-mono-label tracking-wide border transition-all ${
              window === w.key
                ? "bg-navy text-white border-navy"
                : "bg-background text-muted-foreground border-border hover:border-navy"
            }`}
            data-testid={`advisor-window-${w.key}`}
          >
            {w.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-border lg:col-span-2 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="w-52">
                  <div className="flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5" strokeWidth={2} />
                    <span>Color</span>
                  </div>
                </TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {advisors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    No advisors yet. Add one on the right or import from DMS.
                  </TableCell>
                </TableRow>
              )}
              {advisors.map((a) => {
                // MTD $ recovered = scheduled × avg RO ($180). YTD = declines × avg × recovery rate (35%).
                // Rough estimator so James can eyeball performance during the pitch;
                // when we wire /metrics/overview per-advisor these become real numbers.
                const m = metricsByAdvisor[a.id] || {};
                const mtdDollars = (m.scheduled || 0) * 180;
                const ytdDollars = Math.round((m.total_declines || 0) * 180 * 0.35);
                const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n}`;
                return (
                <TableRow key={a.id} data-testid={`advisor-${a.id}`}>
                  <TableCell>
                    <div
                      className="w-3.5 h-3.5 rounded-full ring-2 ring-background shadow"
                      style={{ backgroundColor: a.color || "#0f2b6b" }}
                      data-testid={`advisor-color-dot-${a.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{a.name}</div>
                    <div className="flex gap-1.5 mt-1.5" data-testid={`advisor-perf-${a.id}`}>
                      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[0.65rem]">
                        <span className="font-mono-label text-emerald-800 tracking-widest">MTD</span>
                        <span className="font-bold tabular-nums text-emerald-900">{fmt(mtdDollars)}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md border border-navy/30 bg-navy/5 px-1.5 py-0.5 text-[0.65rem]">
                        <span className="font-mono-label text-navy tracking-widest">YTD</span>
                        <span className="font-bold tabular-nums text-navy">{fmt(ytdDollars)}</span>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{a.email || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{a.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1" data-testid={`advisor-palette-${a.id}`}>
                      {PALETTE.slice(0, 8).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => updateColor(a.id, c)}
                          className={`w-4 h-4 rounded-full ring-2 transition-all ${
                            (a.color || "#0f2b6b") === c
                              ? "ring-foreground ring-offset-1 ring-offset-background"
                              : "ring-transparent hover:ring-muted-foreground/40"
                          }`}
                          style={{ backgroundColor: c }}
                          aria-label={`Change ${a.name} color`}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/advisor-scorecard/${a.id}`} data-testid={`scorecard-link-${a.id}`}>
                        <Button size="sm" variant="outline" title="View scorecard">
                          <BarChart3 className="w-4 h-4" strokeWidth={1.75} />
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(a.id)}
                        data-testid={`delete-advisor-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <Card className="p-5 border-border">
          <div className="font-mono-label text-muted-foreground">New Advisor</div>
          <h2 className="font-display text-lg font-bold tracking-tight mt-1 mb-4">Add to team</h2>
          <form onSubmit={add} className="space-y-3" data-testid="add-advisor-form">
            <div>
              <Label htmlFor="a-name" className="font-mono-label">Name</Label>
              <Input
                id="a-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="advisor-name-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="a-email" className="font-mono-label">Email</Label>
              <Input
                id="a-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="advisor-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="a-phone" className="font-mono-label">Phone</Label>
              <Input
                id="a-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="advisor-phone-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="font-mono-label">Color</Label>
              <div className="mt-2 flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full ring-2 ring-foreground/20 shadow"
                  style={{ backgroundColor: color }}
                />
                <ColorSwatch value={color} onChange={setColor} testid="new-advisor-palette" />
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="add-advisor-submit">
              <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
              Add advisor
            </Button>
          </form>
        </Card>
      </div>
      <ScrollToTopButton testid="advisors-scroll-top" />
    </DashboardShell>
  );
}
